import { useTranslation } from "react-i18next";
import { Button } from "@/core/ui";
import { allDuels } from "@/duels/registry";
import { allGames } from "@/games/registry";

/**
 * Which games and duels the tenant offers, and in what order the games page lists them
 * (ADR 0075, 0076).
 * Picked games come first, in their order; the others follow, unticked. A picked key this
 * build has no player for is kept as it is, so saving never drops it silently.
 */
export function GamesPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (keys: string[]) => void;
}) {
  const { t } = useTranslation();
  const games = [...allGames(), ...allDuels()];
  const byKey = new Map(games.map((game) => [game.key, game]));
  const rows = [
    ...value.filter((key) => byKey.has(key)),
    ...games.map((game) => game.key).filter((key) => !value.includes(key)),
  ];

  const name = (key: string) => t("name", { ns: byKey.get(key)?.namespace });
  const toggle = (key: string, on: boolean) =>
    onChange(on ? [...value, key] : value.filter((k) => k !== key));
  const move = (key: string, by: -1 | 1) => {
    const keys = [...value];
    const from = keys.indexOf(key);
    const to = from + by;
    if (from < 0 || to < 0 || to >= keys.length) return;
    [keys[from], keys[to]] = [keys[to], keys[from]];
    onChange(keys);
  };

  return (
    <fieldset className="bt-admin__role-fieldset">
      <legend className="bt-label">{t("admin.settings.games")}</legend>
      {rows.length === 0 ? (
        <p className="bt-field__hint">{t("admin.settings.games_none")}</p>
      ) : (
        <ol className="bt-admin__game-picker">
          {rows.map((key) => {
            const position = value.indexOf(key);
            const picked = position >= 0;
            return (
              <li key={key} className="bt-admin__game-row">
                <label className="bt-admin__role-toggle">
                  <input
                    type="checkbox"
                    checked={picked}
                    onChange={(e) => toggle(key, e.target.checked)}
                  />
                  {name(key)}
                  <span className="bt-admin__game-tagline">
                    {t("tagline", { ns: byKey.get(key)?.namespace })}
                  </span>
                </label>
                {picked ? (
                  <span className="bt-admin__game-moves">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={position === 0}
                      aria-label={t("admin.settings.games_move_up", { name: name(key) })}
                      onClick={() => move(key, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={position === value.length - 1}
                      aria-label={t("admin.settings.games_move_down", { name: name(key) })}
                      onClick={() => move(key, 1)}
                    >
                      ↓
                    </Button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
      <p className="bt-field__hint">{t("admin.settings.games_hint")}</p>
    </fieldset>
  );
}
